/*
 * FOR SALE — badge « Nouveau » des annonces (direction « Hangar »), calculé à l'heure du visiteur.
 *
 * Module ES partagé : la première partie (« noyau ») est pure, sans DOM, importée par les tests Node
 * (tools/universe-lab/hangar-bay.test.mjs) ; la seconde (« interface ») ne s'exécute que dans un navigateur.
 *
 * CONTRAT
 * - Règle : nouvelle = publiée depuis 7 jours au plus (maintenant − date ≤ 7 × 24 h, glissant), même source et même seuil
 *   que la facette « Nouveautés (7 jours) » de fs-filters.js : date = identifiant Discord du message de départ
 *   (snowflake horodaté, postedTime ci-dessous, identique à celui de fs-filters.js, parité vérifiée par les tests) ;
 *   seuil = jours de la facette « recent » de la configuration des filtres (#fs-listing-filters-config) quand elle est
 *   dans la page, sinon NEW_DAYS. Jamais en Liquid : la page est mise en cache par Shopify, un badge calculé au rendu
 *   deviendrait faux sans republication.
 * - Jamais sur une annonce réservée ou vendue, d'exemple, ou sans date (annonces déposées sur le site : identifiant non
 *   daté). Le Liquid (fs-listing-bay) n'émet déjà pas l'emplacement dans ces cas ; ce module revérifie l'ancêtre.
 * - Emplacement : [data-fs-new="<identifiant daté>"] présent et masqué (hidden) dans le HTML : aucun décalage de mise en
 *   page. Révélé ici (hidden retiré, data-fs-new-on posé sur la racine de l'annonce).
 * - Racine de l'annonce : l'ancêtre [data-fs-new-root], sinon [data-fs-listing], sinon [data-fs-ld] (fiche).
 *   Nom accessible : chaque [data-fs-new-name] de la racine reçoit « , nouvelle annonce » à la fin de son aria-label
 *   (une seule fois). Date relative : chaque [data-fs-posted-text] de la racine (ou [data-fs-posted-text="<id>"] avec
 *   son propre identifiant) reçoit « Publiée aujourd'hui / hier / il y a 3 jours / le 23 août sur Discord » (langue de la
 *   page, fs-i18n.js) ; son texte serveur reste sinon (repli sans script).
 * - window.FSNew : { days(), postedTime(id), isNew(id, opts), relative(id, opts), scan(root), count(root) } ; scan()
 *   est idempotent (à rappeler après l'insertion d'annonces clonées : barre de comparaison, face-à-face).
 * - Écritures limitées à hidden, data-fs-new-on, aria-label des [data-fs-new-name] et texte des [data-fs-posted-text] ;
 *   la zone [data-fs-commerce] n'est jamais touchée. Aucune requête réseau.
 */

import { t, date, locale, relative as relativeTime } from './fs-i18n.js';

/* ============================== Noyau pur ============================== */

/** Fenêtre « Nouveau » par défaut, en jours (config/listing-filters/common.json, facette nouveautes : même valeur). */
export const NEW_DAYS = 7;
const DAY = 86400000;

/**
 * Date de publication d'une annonce Discord : l'identifiant du message de départ est un « snowflake » horodaté
 * (millisecondes depuis 2015 dans ses 42 bits de poids fort). Identifiant absent ou non daté (annonce du site,
 * exemple) → null : l'annonce n'est jamais comptée comme nouvelle. Même calcul que postedTime de fs-filters.js.
 */
const DISCORD_EPOCH = 1420070400000;
export function postedTime(posted) {
  const text = String(posted ?? '').trim();
  if (!/^\d{15,20}$/.test(text)) return null;
  const time = Number(BigInt(text) >> 22n) + DISCORD_EPOCH;
  return Number.isFinite(time) ? time : null;
}

/**
 * Annonce nouvelle ? opts : { now (ms, défaut Date.now()), days (défaut NEW_DAYS), state (data-fs-price-state),
 * sample (annonce d'exemple) }. Réservée, vendue, exemple ou sans date : jamais. Date dans le futur (horloge du visiteur
 * en retard) : nouvelle, comme la facette (now − date ≤ fenêtre).
 */
export function isNew(posted, { now = Date.now(), days = NEW_DAYS, state = '', sample = false } = {}) {
  if (sample || state === 'reserved' || state === 'sold') return false;
  const time = postedTime(posted);
  if (time === null) return false;
  return now - time <= days * DAY;
}

/**
 * Date relative dans la langue de la page (Intl.RelativeTimeFormat) : « aujourd’hui », « hier », « il y a 3 jours »
 * (moins de 8 jours), sinon listing.posted.on_date (« le 23 août », « le 1er octobre » ; « on August 23 ») ; null sans date.
 */
export function relative(posted, { now = Date.now() } = {}) {
  const time = postedTime(posted);
  if (time === null) return null;
  const days = Math.floor((now - time) / DAY);
  // « aujourd’hui », « hier » (Intl, numeric auto), puis toujours « il y a N jours » (jamais « avant-hier »).
  if (days < 2) return relativeTime(-Math.max(0, days), 'day').replace(/'/g, '’');
  if (days < 8) return new Intl.RelativeTimeFormat(locale, { numeric: 'always' }).format(-days, 'day');
  let day = date(time, { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' });
  if (locale === 'fr') day = day.replace(/^1 /, '1er ');
  return t('listing.posted.on_date', { date: day });
}

/* ============================== Interface ============================== */

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  const ROOT = '[data-fs-new-root], [data-fs-listing], [data-fs-ld]';
  const SUFFIX = t('listing.posted.new_suffix');

  /* Seuil : celui de la facette « recent » des filtres si la configuration est dans la page (une seule source). */
  let windowDays = null;
  const days = () => {
    if (windowDays !== null) return windowDays;
    windowDays = NEW_DAYS;
    try {
      const config = JSON.parse(document.getElementById('fs-listing-filters-config')?.textContent || 'null');
      const facet = config?.common?.facets?.find((item) => item.type === 'recent');
      if (Number.isFinite(facet?.days) && facet.days > 0) windowDays = facet.days;
    } catch { /* configuration absente ou illisible : NEW_DAYS */ }
    return windowDays;
  };

  const rootOf = (el) => el.closest(ROOT) || el.parentElement;
  const stateOf = (root) => root?.closest('[data-fs-price-state]')?.getAttribute('data-fs-price-state') || '';
  const sampleOf = (root) => Boolean(root?.closest('[data-fs-sample]'));

  const scan = (scope = document) => {
    const now = Date.now();
    let shown = 0;
    scope.querySelectorAll('[data-fs-new]').forEach((badge) => {
      const root = rootOf(badge);
      const on = isNew(badge.getAttribute('data-fs-new'), { now, days: days(), state: stateOf(root), sample: sampleOf(root) });
      badge.hidden = !on;
      if (!on || !root) return;
      shown += 1;
      root.setAttribute('data-fs-new-on', '');
      root.querySelectorAll('[data-fs-new-name]').forEach((named) => {
        const label = named.getAttribute('aria-label');
        if (label && !label.endsWith(SUFFIX)) named.setAttribute('aria-label', label + SUFFIX);
      });
    });
    scope.querySelectorAll('[data-fs-posted-text]').forEach((el) => {
      const own = el.getAttribute('data-fs-posted-text');
      const posted = own || rootOf(el)?.querySelector('[data-fs-new]')?.getAttribute('data-fs-new') || rootOf(el)?.getAttribute('data-fs-posted');
      const text = relative(posted, { now });
      if (text) el.textContent = t('listing.posted.text', { when: text });
    });
    return shown;
  };
  const count = (scope = document) => scope.querySelectorAll('[data-fs-new]:not([hidden])').length;

  window.FSNew = { days, postedTime, isNew: (posted, opts = {}) => isNew(posted, { days: days(), ...opts }), relative, scan, count };
  const start = () => { try { scan(); } catch (error) { console.warn('[Space Syndicate] badge Nouveau indisponible', error); } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  document.addEventListener('shopify:section:load', (event) => scan(event.target || document));
}
